import React, { useMemo, useEffect, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import { useLatest } from 'ahooks';
import MultiDataSourceTopChartsTemplate from '../MultiDataSourceTopChartsTemplate';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useMarketFilterApi, { MarketSpecailType } from '@intelligence/hooks/useMarketSelector';
import useSenTowerTag from '@intelligence/hooks/useSenTowerTag';
import useEarliesTimeHasData from '@intelligence/hooks/useEarliesTimeHasData';
import useFilterTagsOptions from '@intelligence/hooks/useFilterTagsOptions';
import useMobileGameEngineOptions from '@intelligence/hooks/useMobileGameEngineOptions';
import useHotTagsOptions from '@intelligence/hooks/useHotTagsOptions';
import useMultiDataSourceTopChartsPage from '../../_hooks/useMultiDataSourceTopChartsPage';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { APPMAGIC_TAG_MAP, APPMAGIC_TAG_TITLE_MAP } from '@intelligence/components/Filter/FilterBase/_config';
import { handleActiveUsersFilterParamsToApi } from '@intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import { GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { getMobileTopChartsFilterTips } from '@intelligence/pages/TopCharts/MobileGames/_config/mobileTopChartsFilterTips';

import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import type { IMultiDataSourceTopChartsPageConfig } from '../../types';
import type { IMultiDataSourceTopChartsTemplateNotificationsProps } from '../MultiDataSourceTopChartsTemplate';

const entityType = 'mobile';

interface IMultiDataSourceTopChartsPageProps extends IBaseProps, IMultiDataSourceTopChartsTemplateNotificationsProps {
  pageConfig: IMultiDataSourceTopChartsPageConfig;
  /** 当前入口页面的筛选器继承作用域；不传时保持共享页面原有 common 行为。 */
  pageId?: string;
  /**
   * 禁用筛选器和游戏选择跨Tab继承
   * 当为 true 时，切换Tab不会继承Redux中的筛选器值和游戏选择
   * 默认为 false（保持现有继承行为）
   */
  disableInheritance?: boolean;
}

const MultiDataSourceTopChartsPage: React.FC<IMultiDataSourceTopChartsPageProps> = (props) => {
  const { pageConfig, pageId, disableInheritance, ...restProps } = props;

  const { locale, formatMessage } = useIntl();
  const isCn = locale === LANGUAGE_CN;
  const { loadingStd: definitionsLoading, genreMap, genreOptionsMap, filterTags } = useQueryDefinition();
  const { filterLoading, regionOptions } = useMarketFilterApi({ filterType: MarketSpecailType.common });
  const { tagLoading, tagConfig } = useSenTowerTag({});
  const { latestTimeTypeHasData } = useEarliesTimeHasData({ filterType: pageConfig.latestTimeTypeHasDataFilter });
  const { steamTagOptions, gameModeOptions, isLoading: tagsLoading } = useFilterTagsOptions();
  const { mobileGameEngineOptions } = useMobileGameEngineOptions();
  // 热门标签选项，url 不传则自动从当前页面获取
  // filterTagName 用于确定 HotTagsSource 的默认选中项（与 URL filter_tag_name 对应）
  const { hotTagsOptions, filterTagName, hotTagsLoading } = useHotTagsOptions('mobile');

  const { urlCustomParams, shortLinkLoading, shortLinkValidQuery, shortLinkFilterValue: shortLinkFilterValueRaw } =
    useMultiDataSourceTopChartsPage(pageConfig, { loading: definitionsLoading, genreOptionsMap });

  // 当 URL 有 filter_tag_name 且 hotTagsOptions 加载完成时，
  // 找到匹配选项，用 parseUrlFilterParams 解析其 value，覆盖 shortLinkFilterValue
  const [hotTagFilterValue, setHotTagFilterValue] = useState<any>(null);
  const [hotTagDefaultOption, setHotTagDefaultOption] = useState<any>(null);
  const hotTagResolvedInit = !filterTagName;
  const [hotTagResolved, setHotTagResolved] = useState(hotTagResolvedInit);
  const genreOptionsMapRef = useLatest(genreOptionsMap);
  const filterTagNameRef = useLatest(filterTagName);
  useEffect(() => {
    if (!filterTagName || hotTagsLoading) return;

    if (!hotTagsOptions.length) {
      setHotTagResolved(true);
      return;
    }

    const matchedOption = hotTagsOptions.find((opt) => opt?.name === filterTagName);
    if (!matchedOption?.value) {
      setHotTagResolved(true);
      return;
    }

    let cancelled = false;
    setHotTagDefaultOption(matchedOption);
    parseUrlFilterParams(matchedOption.value, { genreOptionsMap: genreOptionsMapRef.current })
      .then((parsedValue) => {
        if (cancelled) return;
        if (parsedValue && Object.keys(parsedValue).length > 0) {
          // 将 hotTagsSource 的选中值一并写入，确保筛选器 UI 能正确回显选中状态
          parsedValue[filterKeyMap.HOT_TAGS_SOURCE] = { value: matchedOption };
          setHotTagFilterValue(parsedValue);
        }
        setHotTagResolved(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[MultiDataSourceTopChartsPage] hotTag parseUrlFilterParams failed:', err);
        setHotTagResolved(true);
      });

    return () => {
      cancelled = true;
      // 仅在新 filterTagName 为空时才重置 hotTagResolved
      // 避免新 effect 的 parseUrlFilterParams 尚未完成时，
      // Template 的 useEffect 被提前触发并用 null 覆盖 shortLinkFilterValue
      if (!filterTagNameRef.current) {
        setHotTagResolved(true);
      }
    };
  }, [filterTagName, hotTagsLoading, hotTagsOptions]);

  // hotTag 解析完成时覆盖 shortLinkFilterValue；否则使用原始值
  const shortLinkFilterValue = hotTagFilterValue ?? shortLinkFilterValueRaw;

  // 处理App Magic Tags数据
  const appMagicTagConfig = useMemo(() => {
    if (!filterTags) return {};

    const config: IRecord = {};

    const mapTagData = (tags: any[]) => {
      if (!Array.isArray(tags) || tags.length === 0) {
        return [];
      }

      return tags.map((tag) => {
        const tagValue = tag?.tag || '';
        const tagEN = tag?.tag_en || '';
        const tagCN = tag?.tag_cn || '';
        const tagDefault = tagValue || tagEN || tagCN;

        return {
          value: tagDefault,
          text: (isCn ? tagCN : tagEN) || tagDefault,
          textCN: tagCN || tagDefault,
          textEN: tagEN || tagDefault,
        };
      });
    };

    Object.keys(APPMAGIC_TAG_MAP).forEach((key) => {
      const apiFieldName = APPMAGIC_TAG_MAP[key];
      const tagData = filterTags[apiFieldName];

      config[key] = {
        options: mapTagData(tagData),
        title: APPMAGIC_TAG_TITLE_MAP[key],
      };
    });

    return config;
  }, [isCn, filterTags]);

  const defaultParams = {
    ...pageConfig.defaultParams,
    entity_type: entityType,
    ratio_type: 0,
    order: 'desc',
    platforms: ['appstore', 'googleplay'],
  };

  const defaultFilterValue = {
    ...pageConfig.filterCfg.defaultValue,
    [filterKeyMap.RANGE_TIME]: {
      type: 'date',
      value: ['', ''],
    },
  };

  const filterCfg: TopchartFilterCfgType = {
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    hasUrlShortLink: pageConfig.hasUrlShortLink,
    filterKeyList: pageConfig.filterCfg.filterKeyList,
    filterGroups: pageConfig.filterCfg.filterGroups,
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'date',
        value: [moment().subtract(30, 'days'), moment().subtract(1, 'days')],
      },
    },
    companyFilterParams: {
      entity_type: entityType,
      data_sources: pageConfig.filterCfg.companyFilterParamsDataSources,
      source: 'sensortower',
      metric: pageConfig.filterCfg.companyFilterParamsMetric,
      source_metric: pageConfig.filterCfg.companyFilterParamsSourceMetric,
      region_type: 'region',
      regions: ['global'],
      granularity: pageConfig.filterCfg.companyFilterParamsGranularity,
      start_date: '',
      end_date: '',
      search_name: '',
      page: 1,
      page_size: 500,
    },
    specialParams: {
      ...pageConfig.filterCfg.specialParams,
      entityType,
      regionOptions,
      isStdGenreValuesHandler: true,
      genreOverlayClassName: 'cascade-mode',
      // genreOverlayPlacement: 'bottom-end',
      genreOptionsLoading: definitionsLoading,
      genreOptions: genreMap,
      genreSources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
      disableIegg: false,
      disableAnnie: false,
      disableSensorTower: false,
      disableNoChina: false,
      defalutMarket: ['global'],
      rangeTimeType: FilterTimeType.commonRange,
      sensorTowerTagConfig: tagConfig,
      ...appMagicTagConfig,
      steamTagOptions,
      gameModeOptions,
      gameEngineOptions: mobileGameEngineOptions,
      hotTagsOptions,
      // URL filter_tag_name 对应的热门标签 name，用于默认选中 HotTagsSource 对应选项
      filterTagName,
      // URL filter_tag_name 匹配到的默认 hotTag option，用于 Template 初始化 selectedHotTagOption
      hotTagDefaultOption,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      filterTips: getMobileTopChartsFilterTips(formatMessage),
    },
    handleFilterToApiParams: (params: any) => handleActiveUsersFilterParamsToApi(params, entityType),
    enableSticky: pageConfig.filterCfg.enableSticky,
    stickyHeaderTitle: pageConfig.filterCfg.stickyHeaderTitle
      ? formatMessage({ id: pageConfig.filterCfg.stickyHeaderTitle as string })
      : undefined,
  };

  usePageLog(pageConfig.pageLogKey);

  return (
    <MultiDataSourceTopChartsTemplate
      {...restProps}
      entityType={entityType}
      isDataBrain={pageConfig.isDataBrain}
      topChartsType={pageConfig.topChartsType}
      topchartsNameParams={pageConfig.topchartsNameParams}
      pageLogKey={pageConfig.pageLogKey}
      downloadLogKey={pageConfig.downloadLogKey}
      metrics={pageConfig.metrics}
      metric={pageConfig.metric}
      filterLoading={filterLoading || tagLoading || shortLinkLoading || tagsLoading || !hotTagResolved}
      filterCfg={filterCfg}
      defaultParams={defaultParams}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      genreOptionsMap={genreOptionsMap}
      disableInheritance={disableInheritance}
      pageId={pageId}
    />
  );
};

export default MultiDataSourceTopChartsPage;
